import React, {createContext, ReactNode, useContext, useEffect, useReducer, useRef} from 'react';
import {Coupon, MenuItem, MenuItemOption, OrderItem, ShippingInfo} from '../types';
import {useMerchant} from './MerchantContext';
import { trackProductAdded } from '../services/SuggestionService';
import {
  captureCartRecoveryOpportunity,
  captureCartRecoveryOpportunityOnUnload,
  flushPendingSalesRecoveryCaptures,
  requestBrowserRecoveryNotifications,
  resetSalesRecoveryCartCode
} from '../services/salesRecoveryService';

// Define the shape of the cart state
interface CartState {
  items: OrderItem[];
  total: number;
  restaurantId: string | null;
  cashbackBalance: number;
  appliedCashback: number;
  shipping: ShippingInfo | null;
  deliveryFee: number;
  coupon: Coupon | null;
  recommendedItems: MenuItem[];
}

// Define the actions that can be performed on the cart
type CartAction =
  | { type: 'ADD_ITEM'; payload: { menuItem: MenuItem; quantity: number; options?: MenuItemOption[]; notes?: string } }
  | { type: 'REMOVE_ITEM'; payload: { index: number } }
  | { type: 'UPDATE_QUANTITY'; payload: { index: number; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'SET_RESTAURANT'; payload: { restaurantId: string } }
  | { type: 'SET_CASHBACK_BALANCE'; payload: { balance: number } }
  | { type: 'APPLY_CASHBACK'; payload: { amount: number } }
  | { type: 'REMOVE_CASHBACK' }
  | { type: 'SET_SHIPPING'; payload: { shipping: ShippingInfo } }
  | { type: 'SET_DELIVERY_FEE'; payload: { fee: number } }
  | { type: 'REMOVE_SHIPPING' }
  | { type: 'APPLY_COUPON'; payload: { coupon: Coupon } }
  | { type: 'REMOVE_COUPON' }
  | { type: 'SYNC_STOCK'; payload: { items: MenuItem[] } }
  | { type: 'SET_RECOMMENDED_ITEMS'; payload: { items: MenuItem[] } };

// Define the context type
interface CartContextType {
  state: CartState;
  addItem: (menuItem: MenuItem, quantity: number, options?: MenuItemOption[], notes?: string) => void;
  removeItem: (index: number) => void;
  updateQuantity: (index: number, quantity: number) => void;
  clearCart: () => void;
  setRestaurant: (restaurantId: string) => void;
  setCashbackBalance: (balance: number) => void;
  applyCashback: (amount: number) => void;
  removeCashback: () => void;
  setShipping: (shipping: ShippingInfo) => void;
  setDeliveryFee: (fee: number) => void;
  removeShipping: () => void;
  applyCoupon: (coupon: Coupon) => void;
  removeCoupon: () => void;
  setRecommendedItems: (items: MenuItem[]) => void;
}

// Create the context
const CartContext = createContext<CartContextType | undefined>(undefined);

// LocalStorage key for cart state
const CART_STORAGE_KEY = 'cart-state';

// Function to get initial state from localStorage or use default
const getInitialState = (): CartState => {
  if (typeof window === 'undefined') {
    return {
      items: [],
      total: 0,
      restaurantId: null,
      cashbackBalance: 0,
      appliedCashback: 0,
      shipping: null,
      deliveryFee: 0,
      coupon: null,
      recommendedItems: [],
    };
  }

  const savedCart = localStorage.getItem(CART_STORAGE_KEY);
  if (savedCart) {
    try {
      const parsed = JSON.parse(savedCart);
      return {
        ...parsed,
        deliveryFee: parsed.deliveryFee || 0
      };
    } catch (error) {
      console.error('Failed to parse cart from localStorage:', error);
    }
  }

  return {
    items: [],
    total: 0,
    restaurantId: null,
    cashbackBalance: 0,
    appliedCashback: 0,
    shipping: null,
    deliveryFee: 0,
    coupon: null,
    recommendedItems: [],
  };
};

// Initial state
const initialState: CartState = getInitialState();

// Reducer function to handle cart actions
const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const { menuItem, quantity, options, notes } = action.payload;

      // Calculate the item price including options
      const optionsPrice = options?.reduce((sum, option) => sum + option.price, 0) || 0;
      const itemPrice = menuItem.price + optionsPrice;

      // Check if the item already exists in the cart
      const existingItemIndex = state.items.findIndex(item => {
        // Check if it's the same menu item
        if (item.menuItemId !== menuItem.id) return false;

        // Check if it has the same notes
        if (item.notes !== notes) return false;

        // Check if it has the same options
        const itemOptions = item.options || [];
        const newOptions = options || [];

        // If the number of options is different, it's not the same item
        if (itemOptions.length !== newOptions.length) return false;

        // Check if all options match
        return newOptions.every(newOption => 
          itemOptions.some(itemOption => 
            itemOption.id === newOption.id
          )
        );
      });

      // If the item already exists, update its quantity
      if (existingItemIndex !== -1) {
        const existingItem = state.items[existingItemIndex];
        const newItems = [...state.items];

        // Update the existing item's quantity
        newItems[existingItemIndex] = {
          ...existingItem,
          quantity: existingItem.quantity + quantity
        };

        // Calculate the new total
        const newTotal = state.total + (itemPrice * quantity);

        return {
          ...state,
          items: newItems,
          total: newTotal,
        };
      }

      // If the item doesn't exist, create a new one
      const newItem: OrderItem = {
        menuItemId: menuItem.id,
        categoryId: menuItem.categoryId,
        name: menuItem.name,
        quantity,
        price: itemPrice,
        imageUrl: menuItem.image,
        options,
        notes,
        available: menuItem.available,
        status: menuItem.status,
        stockQuantity: menuItem.inventory?.stockQuantity,
        inventory: menuItem.inventory
      };

      // Calculate the new total
      const newTotal = state.total + (itemPrice * quantity);

      return {
        ...state,
        items: [...state.items, newItem],
        total: newTotal,
      };
    }

    case 'REMOVE_ITEM': {
      const { index } = action.payload;
      const item = state.items[index];

      if (!item) return state;

      // Calculate the new total
      const newTotal = state.total - (item.price * item.quantity);

      // Remove the item from the array
      const newItems = [...state.items];
      newItems.splice(index, 1);

      return {
        ...state,
        items: newItems,
        total: newTotal,
      };
    }

    case 'UPDATE_QUANTITY': {
      const { index, quantity } = action.payload;
      const item = state.items[index];

      if (!item) return state;

      // Calculate the price difference
      const priceDifference = item.price * (quantity - item.quantity);

      // Update the item quantity
      const newItems = [...state.items];
      newItems[index] = {
        ...item,
        quantity,
      };

      return {
        ...state,
        items: newItems,
        total: state.total + priceDifference,
      };
    }

    case 'CLEAR_CART':
      return {
        items: [],
        total: 0,
        restaurantId: state.restaurantId,
        cashbackBalance: state.cashbackBalance,
        appliedCashback: 0,
        shipping: null,
        deliveryFee: 0,
        coupon: null,
        recommendedItems: [],
      };

    case 'SET_RESTAURANT':
      // If the restaurant changes, clear the cart
      if (state.restaurantId && state.restaurantId !== action.payload.restaurantId) {
        return {
          items: [],
          total: 0,
          restaurantId: action.payload.restaurantId,
          cashbackBalance: 0,
          appliedCashback: 0,
          shipping: null,
          deliveryFee: 0,
          coupon: null,
          recommendedItems: [],
        };
      }

      return {
        ...state,
        restaurantId: action.payload.restaurantId,
      };

    case 'SET_CASHBACK_BALANCE': {
      const { balance } = action.payload;
      return {
        ...state,
        cashbackBalance: balance,
      };
    }

    case 'APPLY_CASHBACK': {
      const { amount } = action.payload;

      // Check if user has enough balance
      if (state.cashbackBalance < amount) {
        return state;
      }

      // Calculate new total after applying cashback
      const newTotal = Math.max(0, state.total - amount);

      return {
        ...state,
        cashbackBalance: state.cashbackBalance - amount,
        appliedCashback: state.appliedCashback + amount,
        total: newTotal,
      };
    }

    case 'REMOVE_CASHBACK': {
      // If no cashback is applied, do nothing
      if (state.appliedCashback <= 0) {
        return state;
      }

      // Recalculate total without the cashback
      const newTotal = state.total + state.appliedCashback;

      return {
        ...state,
        cashbackBalance: state.cashbackBalance + state.appliedCashback,
        appliedCashback: 0,
        total: newTotal,
      };
    }

    case 'SET_SHIPPING': {
      const { shipping } = action.payload;
      return {
        ...state,
        shipping,
      };
    }

    case 'SET_DELIVERY_FEE': {
      const { fee } = action.payload;
      return {
        ...state,
        deliveryFee: fee,
      };
    }

    case 'REMOVE_SHIPPING': {
      return {
        ...state,
        shipping: null,
      };
    }

    case 'APPLY_COUPON': {
      const { coupon } = action.payload;

      // Check if coupon is valid
      if (!coupon.isValid) {
        return state;
      }

      // If we have a calculated discount amount from the API, use it
      if (coupon.discountAmount !== undefined) {
        return {
          ...state,
          coupon,
          total: Math.max(0, state.total - coupon.discountAmount),
        };
      }

      // Fallback to local calculation if discountAmount is not provided
      // Check if order meets minimum value requirement
      if (coupon.minOrderValue && state.total < coupon.minOrderValue) {
        return state;
      }

      // Calculate new total after applying coupon
      let newTotal = state.total;

      if (coupon.discountValue) {
        newTotal = Math.max(0, newTotal - coupon.discountValue);
      } else if (coupon.discountPercentage) {
        newTotal = newTotal * (1 - coupon.discountPercentage / 100);
      }

      return {
        ...state,
        coupon,
        total: newTotal,
      };
    }

    case 'REMOVE_COUPON': {
      // If no coupon is applied, do nothing
      if (!state.coupon) {
        return state;
      }

      // Recalculate total without the coupon
      let newTotal = state.total;
      const coupon = state.coupon;

      if (coupon.discountAmount !== undefined) {
        newTotal = state.total + coupon.discountAmount;
      } else if (coupon.discountValue) {
        newTotal = state.total + coupon.discountValue;
      } else if (coupon.discountPercentage) {
        newTotal = state.total / (1 - (coupon.discountPercentage / 100));
      }

      return {
        ...state,
        coupon: null,
        total: newTotal,
      };
    }

    case 'SYNC_STOCK': {
      const { items } = action.payload;
      const newItems = state.items.map(cartItem => {
        const productInfo = items.find(item => item.id === cartItem.menuItemId);
        if (productInfo) {
          return {
            ...cartItem,
            available: productInfo.available,
            status: productInfo.status,
            stockQuantity: productInfo.inventory?.stockQuantity,
            inventory: productInfo.inventory,
            categoryId: cartItem.categoryId || productInfo.categoryId
          };
        }
        return cartItem;
      });

      return {
        ...state,
        items: newItems
      };
    }

    case 'SET_RECOMMENDED_ITEMS': {
      const { items } = action.payload;
      return {
        ...state,
        recommendedItems: items,
      };
    }

    default:
      return state;
  }
};

// Provider component
interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const { merchantId, restaurant } = useMerchant();
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const stateRef = useRef(state);
  const notificationRequestInFlightRef = useRef(false);
  const activeMerchantId = merchantId || state.restaurantId || restaurant?.id || null;

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Sync stock when restaurant data changes
  useEffect(() => {
    if (restaurant && restaurant.categories) {
      const allProducts: MenuItem[] = [];
      restaurant.categories.forEach(category => {
        category.products?.forEach(product => {
          // Ensure categoryId is passed if missing in product but available in category
          const menuItem: MenuItem = {
            ...product as unknown as MenuItem,
            categoryId: product.categoryId || category.id
          };
          allProducts.push(menuItem);
        });
      });

      if (allProducts.length > 0) {
        dispatch({ type: 'SYNC_STOCK', payload: { items: allProducts } });
      }
    }
  }, [restaurant]);

  // Set restaurantId from merchant context if not set
  useEffect(() => {
    if (merchantId && !state.restaurantId) {
      setRestaurant(merchantId);
    }
  }, [merchantId, state.restaurantId]);

  // Save cart state to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state));
    }
  }, [state]);

  useEffect(() => {
    if (!activeMerchantId || state.items.length === 0) return;

    const timeoutId = window.setTimeout(() => {
      captureCartRecoveryOpportunity(
        activeMerchantId,
        state.items,
        state.total,
        state.coupon?.code
      ).catch(error => {
        console.warn('[SalesRecovery] Could not capture cart recovery opportunity:', error);
      });
    }, 1200);

    return () => window.clearTimeout(timeoutId);
  }, [activeMerchantId, state.items, state.total, state.coupon]);

  useEffect(() => {
    flushPendingSalesRecoveryCaptures().catch(error => {
      console.warn('[SalesRecovery] Could not flush pending recovery captures:', error);
    });

    const handleOnline = () => {
      flushPendingSalesRecoveryCaptures().catch(error => {
        console.warn('[SalesRecovery] Could not flush pending recovery captures:', error);
      });
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  useEffect(() => {
    const flushRecoveryOpportunity = () => {
      const currentState = stateRef.current;
      const currentMerchantId = merchantId || currentState.restaurantId || restaurant?.id || null;
      if (!currentMerchantId || currentState.items.length === 0) return;

      captureCartRecoveryOpportunityOnUnload(
        currentMerchantId,
        currentState.items,
        currentState.total,
        currentState.coupon?.code
      );
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushRecoveryOpportunity();
      }
    };

    window.addEventListener('pagehide', flushRecoveryOpportunity);
    window.addEventListener('beforeunload', flushRecoveryOpportunity);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', flushRecoveryOpportunity);
      window.removeEventListener('beforeunload', flushRecoveryOpportunity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [merchantId, restaurant?.id]);

  const requestRecoveryNotificationsAfterUserAction = (currentMerchantId: string) => {
    if (notificationRequestInFlightRef.current) return;
    notificationRequestInFlightRef.current = true;
    requestBrowserRecoveryNotifications(currentMerchantId)
      .catch(error => {
        console.warn('[SalesRecovery] Could not request recovery notifications:', error);
      })
      .finally(() => {
        notificationRequestInFlightRef.current = false;
      });
  };

  const captureRecoverySnapshot = (cartState: CartState, currentMerchantId: string) => {
    if (cartState.items.length === 0) return;

    captureCartRecoveryOpportunity(
      currentMerchantId,
      cartState.items,
      cartState.total,
      cartState.coupon?.code
    ).catch(error => {
      console.warn('[SalesRecovery] Could not capture cart recovery opportunity:', error);
    });
  };

  // Action creators
  const addItem = (menuItem: MenuItem, quantity: number, options?: MenuItemOption[], notes?: string) => {
    const action: CartAction = { type: 'ADD_ITEM', payload: { menuItem, quantity, options, notes } };
    const nextState = cartReducer(stateRef.current, action);
    const currentMerchantId = merchantId || nextState.restaurantId || restaurant?.id || null;

    dispatch(action);
    stateRef.current = nextState;

    if (currentMerchantId) {
      trackProductAdded(currentMerchantId, menuItem.id);
      captureRecoverySnapshot(nextState, currentMerchantId);
      requestRecoveryNotificationsAfterUserAction(currentMerchantId);
    }
  };

  const removeItem = (index: number) => {
    dispatch({ type: 'REMOVE_ITEM', payload: { index } });
  };

  const updateQuantity = (index: number, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { index, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
    resetSalesRecoveryCartCode();
  };

  const setRestaurant = (restaurantId: string) => {
    dispatch({ type: 'SET_RESTAURANT', payload: { restaurantId } });
  };

  // Action creators for cashback
  const setCashbackBalance = (balance: number) => {
    dispatch({ type: 'SET_CASHBACK_BALANCE', payload: { balance } });
  };

  const applyCashback = (amount: number) => {
    dispatch({ type: 'APPLY_CASHBACK', payload: { amount } });
  };

  const removeCashback = () => {
    dispatch({ type: 'REMOVE_CASHBACK' });
  };

  const setShipping = (shipping: ShippingInfo) => {
    dispatch({ type: 'SET_SHIPPING', payload: { shipping } });
  };

  const setDeliveryFee = (fee: number) => {
    dispatch({ type: 'SET_DELIVERY_FEE', payload: { fee } });
  };

  const removeShipping = () => {
    dispatch({ type: 'REMOVE_SHIPPING' });
  };

  const applyCoupon = (coupon: Coupon) => {
    dispatch({ type: 'APPLY_COUPON', payload: { coupon } });
  };

  const removeCoupon = () => {
    dispatch({ type: 'REMOVE_COUPON' });
  };

  const setRecommendedItems = (items: MenuItem[]) => {
    dispatch({ type: 'SET_RECOMMENDED_ITEMS', payload: { items } });
  };

  const value = {
    state,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    setRestaurant,
    setCashbackBalance,
    applyCashback,
    removeCashback,
    setShipping,
    setDeliveryFee,
    removeShipping,
    applyCoupon,
    removeCoupon,
    setRecommendedItems,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// Custom hook to use the cart context
export const useCart = (): CartContextType => {
  const context = useContext(CartContext);

  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }

  return context;
};
